// Automations: "when this happens, do that" rules.

export type Trigger =
  | "appointment_booked" | "appointment_rescheduled" | "appointment_cancelled" | "appointment_completed" | "appointment_no_show"
  | "appointment_before" | "appointment_after" | "deal_stage_changed" | "contact_created" | "daily";

export type ActionType = "message_customer" | "notify_team" | "create_task" | "daily_digest";

export interface RuleConfig {
  minutes?: number;
  only_attended?: boolean;
  time?: string;
  weekdays?: number[];
  service_ids?: string[];
  pipeline_id?: string | null;
  stage_ids?: string[];
  sources?: string[];
}

export interface RuleAction {
  type: ActionType;
  channel?: "auto" | "whatsapp" | "email";
  text?: string;
  email_subject?: string;
  // Arabic versions, for customers whose language is Arabic.
  text_ar?: string;
  email_subject_ar?: string;
  whatsapp_template_id?: string | null;
  template_vars?: string[];
  member_ids?: string[];
  roles?: string[];
  assigned?: boolean;
  emails?: string[];
  only_own?: boolean;
  day?: "today" | "tomorrow";
  title?: string;
  due_hours?: number | null;
  priority?: "low" | "normal" | "high";
}

export interface Rule {
  id: string;
  name: string;
  enabled: boolean;
  trigger: Trigger;
  trigger_config: RuleConfig;
  actions: RuleAction[];
  source: "manual" | "template" | "system";
  template_key: string | null;
  run_count: number;
  last_run_at: string | null;
  last_error: string | null;
  last_error_at: string | null;
  updated_at: string;
}

// A template's rule: no company-specific ids; it has a key instead.
export interface TemplateRule {
  key: string;
  name: string;
  enabled: boolean;
  trigger: Trigger;
  trigger_config: RuleConfig;
  actions: RuleAction[];
}

export interface Named { id: string; name: string }

export interface RuleOptions {
  variables: Record<Trigger, string[]>;
  services: Named[];
  pipelines: (Named & { stages: Named[] })[];
  members: (Named & { role: string })[];
  whatsapp_templates: (Named & { language: string; variable_count: number })[];
}

export interface AutomationsView {
  rules: Rule[];
  options: RuleOptions;
  customer_reply_email: string | null;
  customer_language: "ar" | "en";
  enabled_in_plan: boolean;
  rules_limit: number | null;
  messages_limit: number | null;
  messages_used: number;
  external_recipients_limit: number;
  can_set_external_recipients: boolean;
}

export interface Run {
  id: string;
  status: "queued" | "done" | "skipped" | "failed";
  contact_id: string | null;
  contact_name: string | null;
  messages: number;
  detail: string | null;
  created_at: string;
  finished_at: string | null;
}

export const roleNames: Record<string, string> = {
  crm_admin: "Admins",
  crm_manager: "Managers",
  crm_employee: "Employees",
  crm_analyst: "Analysts",
};

export const weekdayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Variables usable in each trigger when the server's list isn't loaded
// (the operator's template editor). Mirrors the server.
export function defaultVariables(trigger: Trigger): string[] {
  if (trigger === "daily") return ["business", "date"];
  const common = ["business", "customer_first_name", "customer_name"];
  if (trigger.startsWith("appointment_")) return [...common, "service", "date", "time", "when", "staff"];
  if (trigger === "deal_stage_changed") return [...common, "deal", "stage", "previous_stage"];
  return common;
}

export function allowedActions(trigger: Trigger): ActionType[] {
  return trigger === "daily" ? ["daily_digest", "notify_team"] : ["message_customer", "notify_team", "create_task"];
}

export function blankAction(type: ActionType): RuleAction {
  switch (type) {
    case "message_customer":
      return { type, channel: "auto", text: "", email_subject: "", template_vars: [] };
    case "notify_team":
      return { type, assigned: true, roles: [], member_ids: [], text: "", email_subject: "" };
    case "create_task":
      return { type, title: "", due_hours: 24, priority: "normal", assigned: true };
    case "daily_digest":
      return { type, roles: ["crm_admin"], member_ids: [], emails: [], day: "today", only_own: false };
  }
}

export function defaultConfig(trigger: Trigger): RuleConfig {
  switch (trigger) {
    case "appointment_before":
      return { minutes: 1440 };
    case "appointment_after":
      return { minutes: 60, only_attended: true };
    case "daily":
      return { time: "07:30", weekdays: [0, 1, 2, 3, 4] };
    default:
      return {};
  }
}

// Durations are stored in minutes and shown in the largest whole unit.
export function splitMinutes(m: number): { value: number; unit: "minutes" | "hours" | "days" } {
  if (m % 1440 === 0) return { value: m / 1440, unit: "days" };
  if (m % 60 === 0) return { value: m / 60, unit: "hours" };
  return { value: m, unit: "minutes" };
}

export function durationText(m: number): string {
  const { value, unit } = splitMinutes(m);
  const u = value === 1 ? unit.slice(0, -1) : unit;
  return `${value} ${u}`;
}
